/**
 * Private-channel dedupe.
 *
 * **Public channels are exactly-once within a subscription. Private channels are
 * at-least-once** (docs/04 §9.4). A private event may be delivered twice —
 * across a reconnect, a failover, or a re-auth — and a client that assumes
 * otherwise will double-count a fill. That is not a hypothetical: the re-snapshot
 * after every reconnect deliberately re-delivers everything the client already
 * has.
 *
 * Two mechanisms, one per shape of object:
 *
 * - **Versioned** (`orders`, `fills`, `positions`, `balance`) — apply iff the
 *   ordering key is **strictly greater** than the one held. Never merge fields
 *   from an older frame. This also handles *reordering*, which an id set does
 *   not, and it is why it is the primary mechanism.
 * - **Immutable** (`settlements`, `notifications`) — the object never changes,
 *   so first delivery wins and every later one is dropped on sight of its id.
 *   Backed by a bounded, expiring LRU, because an id set that only grows leaks
 *   across a session that runs for hours at 2,880 markets a day, and one that
 *   only bounds forgets a decision while the thing it decided about is still on
 *   screen.
 *
 * **`fills` moved from immutable to versioned in §F31, and it is the reason to
 * read this comment before adding a channel.** A fill looked immutable for ten
 * phases and was not: under D-B1 it is *matched* at the sequencer and *mined*
 * when its `matchOrders` transaction lands on Arbitrum, and the second tier
 * arrives as the same `fill_id` at a higher `version` carrying
 * `settlement_tx_hash`. Deduping on the id alone returned `DUPLICATE` and the
 * user never learned the money had moved. **An object is immutable only if
 * nothing outside the venue can later say something new about it** — which
 * rules out anything a chain confirms.
 *
 * No wire field is called `event_id` — that name belongs to
 * `GET /portfolio/history`. The LRU here keys on each channel's own immutable id
 * (`settlement_id`, `notification_id`), which is what plan §Phase 2 means by
 * "the 10-minute `event_id` LRU".
 *
 * **There is no cross-channel ordering guarantee**, and the roll depends on that
 * being handled correctly: a `fills` frame can arrive before the `orders` frame
 * that reflects it, and a `balance` frame before the `settlements` frame that
 * caused it. The client must be correct for all 720 arrival orders of the
 * six-frame settlement burst, which it is if and only if each frame is applied
 * by its own version/id rule — plus the one hint at the bottom of this file,
 * which lets a fill stand in for the position frame that has not arrived yet.
 */

import { TtlLru, EVENT_ID_TTL_MS } from '@/lib/lru'
import type { ChannelName } from './protocol'
import { parseChannel, type DedupeStrategy } from './channels'

export type DedupeVerdict =
  /** Not seen before, or strictly newer than what is held. Apply it. */
  | 'NEW'
  /** Same id, not newer. Discard silently — this is the correct behaviour, not
   *  a degradation. */
  | 'STALE'
  /** Same id, already applied. An at-least-once redelivery. */
  | 'DUPLICATE'
  /** The payload carries no id, so nothing can be said about it. Applied, and
   *  counted: a private frame without its dedupe key is a spec violation the
   *  client should be able to see in a test rather than infer from a doubled
   *  balance. */
  | 'UNKEYED'

export interface DedupeStats {
  accepted: number
  stale: number
  duplicates: number
  unkeyed: number
}

/** Ids retained per immutable channel. 1,000 fills is more than an hour of the
 *  busiest realistic trading (docs/04 §9.4); the other two are far quieter. */
export const DEFAULT_ID_WINDOW = 1_000

export interface ChannelDedupeOptions {
  /** Monotonic. `Date.now()` lives only in `lib/clock` (D9), and an expiry
   *  window driven by a wall clock a user can change is one that can be made to
   *  forget on demand. */
  now: () => number
  idWindow?: number
  ttlMs?: number
}

/**
 * One channel's dedupe state.
 *
 * Holds either a version map or an id LRU, never both: the strategy is a
 * property of the channel (`channels/`), so a channel cannot drift into being
 * deduped two ways in two code paths.
 */
export class ChannelDedupe {
  readonly channel: ChannelName
  readonly strategy: DedupeStrategy
  readonly #idField: string | undefined
  readonly #orderField: string | undefined
  readonly #versions = new Map<string, number>()
  readonly #ids: TtlLru<string, true> | null

  #stats: DedupeStats = { accepted: 0, stale: 0, duplicates: 0, unkeyed: 0 }

  constructor(channel: ChannelName, options: ChannelDedupeOptions) {
    const descriptor = parseChannel(channel)
    this.channel = channel
    this.strategy = descriptor.dedupe
    this.#idField = descriptor.idField
    this.#orderField = descriptor.orderField
    this.#ids =
      descriptor.dedupe === 'immutable'
        ? new TtlLru<string, true>({
            max: options.idWindow ?? DEFAULT_ID_WINDOW,
            ttlMs: options.ttlMs ?? EVENT_ID_TTL_MS,
            now: options.now,
          })
        : null
  }

  get stats(): DedupeStats {
    return { ...this.#stats }
  }

  get trackedIds(): number {
    return this.#ids?.size ?? this.#versions.size
  }

  /** The ordering value currently held for `id`, or `undefined`. Exposed so a
   *  store can answer "is this frame worth rendering?" without a second map. */
  versionOf(id: string): number | undefined {
    return this.#versions.get(id)
  }

  accept(payload: unknown): DedupeVerdict {
    if (this.strategy === 'sequence') return 'NEW'
    const record = payload as Record<string, unknown> | null
    if (typeof record !== 'object' || record === null || !this.#idField) return this.#count('UNKEYED')

    const rawId = record[this.#idField]
    const id = typeof rawId === 'string' ? rawId : typeof rawId === 'number' ? String(rawId) : null
    if (id === null) return this.#count('UNKEYED')

    if (this.strategy === 'immutable') {
      // `add` is one operation on purpose: a `has` followed by a `set` is two
      // chances to get the order wrong at the one call site that matters.
      return this.#count(this.#ids?.add(id, true) === false ? 'DUPLICATE' : 'NEW')
    }

    const order = this.#orderField === undefined ? undefined : record[this.#orderField]
    if (typeof order !== 'number') {
      // A versioned object with no version cannot be ordered against what is
      // held. Applying it is the safer half of the trade — the alternative is a
      // balance that stops updating because one frame lost a field.
      this.#versions.set(id, Number.NEGATIVE_INFINITY)
      return this.#count('UNKEYED')
    }

    const held = this.#versions.get(id)
    if (held !== undefined && order <= held) {
      return this.#count(order === held ? 'DUPLICATE' : 'STALE')
    }
    this.#versions.set(id, order)
    return this.#count('NEW')
  }

  /**
   * Seed from a REST snapshot.
   *
   * Every reconnect re-snapshots the private side unconditionally, and every
   * one of those rows is about to arrive again on the socket. Seeding means the
   * redelivery is recognised as one instead of being applied a second time.
   */
  seed(payloads: readonly unknown[]): void {
    for (const payload of payloads) this.accept(payload)
  }

  /**
   * Deliberately **not** cleared on reconnect.
   *
   * The whole point of an at-least-once channel is that the same fill arrives
   * again on the new socket. Clearing here would throw away the only record that
   * says so.
   */
  clear(): void {
    this.#versions.clear()
    this.#ids?.clear()
    this.#stats = { accepted: 0, stale: 0, duplicates: 0, unkeyed: 0 }
  }

  #count(verdict: DedupeVerdict): DedupeVerdict {
    switch (verdict) {
      case 'NEW':
        this.#stats.accepted += 1
        break
      case 'STALE':
        this.#stats.stale += 1
        break
      case 'DUPLICATE':
        this.#stats.duplicates += 1
        break
      case 'UNKEYED':
        this.#stats.unkeyed += 1
        break
    }
    return verdict
  }
}

/** One `ChannelDedupe` per channel, created on demand. */
export class DedupeSet {
  readonly #options: ChannelDedupeOptions
  readonly #byChannel = new Map<ChannelName, ChannelDedupe>()

  constructor(options: ChannelDedupeOptions) {
    this.#options = options
  }

  for(channel: ChannelName): ChannelDedupe {
    const existing = this.#byChannel.get(channel)
    if (existing) return existing
    const created = new ChannelDedupe(channel, this.#options)
    this.#byChannel.set(channel, created)
    return created
  }

  accept(channel: ChannelName, payload: unknown): DedupeVerdict {
    return this.for(channel).accept(payload)
  }

  get channels(): ChannelName[] {
    return [...this.#byChannel.keys()]
  }

  clear(): void {
    for (const dedupe of this.#byChannel.values()) dedupe.clear()
  }
}

// ---------------------------------------------------------------------------
// Cross-channel ordering
// ---------------------------------------------------------------------------

/**
 * The position a fill implies, read off the fill itself.
 *
 * A `fills` frame can arrive before the `positions` frame for the same match,
 * and at sixty fills a minute for an active trader, waiting on another channel —
 * or worse, issuing a query — is a round trip too many. So every fill carries
 * the account's position in that market **after** this fill, and a client can
 * apply it immediately and be right.
 *
 * `post_position` is one nested object in both specs since §F16; `asyncapi`
 * carried the three numbers flattened into `post_position_shares` /
 * `_outcome` / `_avg_price_cents` until then. `net_shares` is **signed** — an
 * explicit `+` for UP, `-` for DOWN — so the sign is not inferred from
 * `outcome`, and `outcome` may be `FLAT`, which is what a fill that closed a
 * position out reports.
 */
export interface PositionHint {
  marketId: string
  /** Signed, with an explicit sign: `+` is UP, `-` is DOWN. Carried as the
   *  string the wire sent — no float ever touches a share count. */
  netShares: string
  /** `UP`, `DOWN`, or `FLAT` when the fill closed the position out. */
  outcome?: string
  /** Cents as a 4dp decimal string. An average of integers is not an integer. */
  avgEntryPriceCents?: string
  costBasis?: string
  /** When the fill executed. The positions store applies a hint only if it has
   *  nothing newer, so this is the comparison key. */
  atMs: number
  /** Present on every fill, so an optimistic local row can be matched to the
   *  server's order without waiting for the `orders` frame. */
  clientOrderId?: string
}

export function positionHintFromFill(fill: unknown): PositionHint | null {
  const record = fill as Record<string, unknown> | null
  if (typeof record !== 'object' || record === null) return null

  const marketId = record.market_id
  const atMs = record.executed_at_ms
  if (typeof marketId !== 'string' || typeof atMs !== 'number') return null

  const position = record.post_position as Record<string, unknown> | undefined
  if (typeof position !== 'object' || position === null) return null

  const netShares = pickString(position.net_shares)
  if (netShares === undefined) return null

  const outcome = pickString(position.outcome)
  const avgEntryPriceCents = pickString(position.avg_entry_price_cents)
  const costBasis = pickString(position.cost_basis)
  const clientOrderId = pickString(record.client_order_id)

  return {
    marketId,
    netShares,
    atMs,
    ...(outcome === undefined ? {} : { outcome }),
    ...(avgEntryPriceCents === undefined ? {} : { avgEntryPriceCents }),
    ...(costBasis === undefined ? {} : { costBasis }),
    ...(clientOrderId === undefined ? {} : { clientOrderId }),
  }
}

function pickString(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) if (typeof candidate === 'string') return candidate
  return undefined
}
